import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { listTickets, Ticket } from '../../../services/ticketsApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './CustomerReturnsTab.module.css';

/**
 * VP-CUST-003 Returns tab.
 *
 * There is no structured returns/RMA backend entity (confirmed — Phase 2
 * scope). Rather than invent one, this surfaces the customer's own support
 * tickets filed under the existing 'returns' category
 * (services/ticketsApi.ts's TicketCategory enum) — a ticket-backed view,
 * not a new backend entity. GET /tickets already scopes Customer-role
 * requests to their own customer_id server-side (routes/tickets.ts).
 */
export const CustomerReturnsTab: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    listTickets({ category: 'returns', limit: 50 })
      .then((res) => setTickets(res.data))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load returns'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.title}>Returns &amp; Disputes</h3>
        <button
          type="button"
          className={styles.newBtn}
          onClick={() => navigate('/customer-report-issue?category=returns')}
        >
          + New Return Request
        </button>
      </div>

      <AsyncBoundary
        loading={loading && tickets.length === 0}
        error={error}
        empty={!loading && tickets.length === 0}
        emptyMessage="No return requests yet"
        onRetry={load}
      >
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>TICKET #</th>
                <th className={styles.th}>ORDER</th>
                <th className={styles.th}>SUBJECT</th>
                <th className={styles.th}>STATUS</th>
                <th className={styles.th}>PRIORITY</th>
                <th className={styles.th}>OPENED</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id}>
                  <td className={styles.td}>{t.ticket_number}</td>
                  <td className={styles.td}>{t.order_id ? formatOrderId(t.order_id) : '—'}</td>
                  <td className={styles.td}>{t.subject}</td>
                  <td className={styles.td}>
                    <span className={styles.badge}>{t.status.replace('_', ' ')}</span>
                  </td>
                  <td className={styles.td}>
                    <span className={styles.badge}>{t.priority}</span>
                  </td>
                  <td className={styles.td}>{new Date(t.opened_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AsyncBoundary>
    </div>
  );
};

export default CustomerReturnsTab;
